import type { ChangeEvent } from 'react'

import { FvButton } from '@fv/client-components'
import { parseCurrency, useCurrencyOptions } from '@fv/client-core'
import type { Currency } from '@fv/models'

import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import { SelectField } from '@/components/inputs/SelectField'
import { Pinline } from '@/components/Pinline'
import { ShipperTooltip } from '@/components/ShipperTooltip'

import type { QuoteEditModel } from '../mini-quote-list/types'

type Props = {
  quote: QuoteEditModel
  onChange: (quote: QuoteEditModel) => void
  locations?: Array<{ country?: string }>
}

export const BaseRateField = ({ quote, onChange, locations }: Props) => {
  const currencyOptions = useCurrencyOptions(locations)
  const isEditingCharge = quote.charges?.filter(
    c => c.name !== 'freightview markup',
  ).length
  const markupCharge = quote.charges?.find(c => c.name === 'freightview markup')
  const charge = isEditingCharge
    ? quote.charges?.find(c => c.name === 'linehaul')
    : undefined
  const value = isEditingCharge
    ? charge?.amount
    : quote.amount - (markupCharge?.amount || 0)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const amount = parseCurrency(e.target.value)

    onChange({
      ...quote,
      ...(isEditingCharge
        ? {
            charges: quote.charges?.map(c =>
              c.name === charge.name ? { ...c, amount } : c,
            ),
          }
        : { amount }),
    })
  }

  return (
    <div className="flex items-center gap-3">
      <div className="italic">Base rate</div>

      <Pinline dashed flex />
      <ShipperTooltip label="You have the option to input either an 'all-in' rate here or a base rate along with itemized charges.">
        <FvButton
          icon="question-circle"
          iconClass="text-fv-orange-bright"
          className="cursor-default"
        />
      </ShipperTooltip>
      <InputGroup
        className="mb-0 w-48"
        inputProps={{
          name: 'base-amount',
          value: value,
          className: 'text-right',
          isPrice: true,
          onChange: handleChange,
        }}
        inputType="amount"
        required
        startContent={<InputAdornment icon="dollar-sign" position="start" />}
        endContent={
          <SelectField
            className="w-auto -ml-px"
            name="base-currency"
            value={quote.currency || 'usd'}
            onChange={e =>
              onChange({
                ...quote,
                currency: e.target.value as Currency,
              })
            }
            options={currencyOptions}
          />
        }
      />
    </div>
  )
}
